// MUI-free copy of Ui-components/ios-development/IosFeatureCards.
import {memo} from 'react';
import type {ReactNode} from 'react';
import parse from 'html-react-parser';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import Container from '@/components/atoms/Container';
import ArrowButton from '@/components/atoms/ArrowButton';
import Grid from '@/components/atoms/Grid';
import SectionHeader from '@/components/molecules/SectionHeader';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './IosFeatureCards.module.scss';

interface FeatureCard {
    title: string;
    /** May carry inline HTML links (`<a href="…" target="_blank">`), parsed at render. */
    points: string[];
    /** Path to a line-icon SVG. Without one, the card shows its number instead. */
    icon?: string;
    /** Internal page the title links to. */
    href?: string;
}

interface IosFeatureCardsProps {
    heading: string;
    subheading: ReactNode;
    cards: FeatureCard[];
    /** With an image, the cards sit two-by-two beside it; without, they run in one row. */
    imageSrc?: string | StaticImageData;
    imageAlt?: string;
    ctaText?: string;
    onCtaClick?: () => void;
    /** Puts the badge and title side by side on one row instead of stacking them. */
    inlineHeader?: boolean;
    /** Drops the orange bullet dot before each point. */
    hideBullets?: boolean;
}

const COVER = {objectFit: 'cover'} as const;

// The supplied icons are black-stroked; a CSS mask recolours them to brand blue.
const iconMask = (src: string) => ({maskImage: `url('${src}')`, WebkitMaskImage: `url('${src}')`});

const renderCard = (card: FeatureCard, idx: number, inlineHeader?: boolean, hideBullets?: boolean) => (
    <div className={styles.card}>
        <div className={inlineHeader ? styles.headerRow : undefined}>
            <div className={`feature-card-badge ${styles.badge}`} aria-hidden="true">
                {card.icon ? (
                    <div className={`feature-card-icon ${styles.icon}`} style={iconMask(card.icon)} />
                ) : (
                    <span className={`font-headers feature-card-text ${styles.number}`}>{String(idx + 1).padStart(2, '0')}</span>
                )}
            </div>
            <h3 className={`font-headers feature-card-text ${styles.title}`}>
                {card.href ? (
                    <Link href={card.href} className={styles.titleLink}>
                        {card.title}
                    </Link>
                ) : (
                    card.title
                )}
            </h3>
        </div>
        <ul className={hideBullets ? `${styles.pointList} ${styles.noBullets}` : styles.pointList}>
            {card.points.map((point) => (
                <li key={point} className={`font-regular-text feature-card-text ${styles.point}`}>
                    {parse(point)}
                </li>
            ))}
        </ul>
    </div>
);

const IosFeatureCards = ({
    heading,
    subheading,
    cards,
    imageSrc,
    imageAlt,
    ctaText,
    onCtaClick,
    inlineHeader,
    hideBullets
}: IosFeatureCardsProps) => (
    <div>
        <Container>
            <SectionHeader heading={heading} subheading={subheading} className={styles.headerWrap} />

            {imageSrc ? (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={5} className={styles.itemFlex}>
                        <div className={styles.imageWrap}>
                            <OptimizedImage
                                src={imageSrc}
                                alt={imageAlt ?? heading}
                                fill
                                sizes="(max-width: 900px) 100vw, 40vw"
                                style={COVER}
                            />
                        </div>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={3}>
                            {cards?.map((card, idx) => (
                                <Grid item xs={12} sm={6} key={card.title} className={styles.itemFlex}>
                                    {renderCard(card, idx, inlineHeader, hideBullets)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={3}>
                    {cards?.map((card, idx) => (
                        <Grid item xs={12} sm={6} md={3} key={card.title} className={styles.itemFlex}>
                            {renderCard(card, idx, inlineHeader, hideBullets)}
                        </Grid>
                    ))}
                </Grid>
            )}

            {ctaText && onCtaClick && (
                <div className={styles.ctaWrap}>
                    <ArrowButton variant="primary" icon="ArrowOutward" bounce={false} onClick={onCtaClick}>
                        {ctaText}
                    </ArrowButton>
                </div>
            )}
        </Container>
    </div>
);

export default memo(IosFeatureCards);
